import { useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';

import clsx from 'clsx';

import { AlbumFunnelModalService } from '@/components/pages/albums/AlbumFunnelModal/services/AlbumFunnelModalService';

import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import { UPDATE_POST_TYPES, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';

import { postsSortTypes, storiesSortTypes } from '@/lib/posts/postSortTypes';
import { INSTAGRAM, PINTEREST, TWITTER } from '@/lib/socialProfile';

import Icon from '@/components/base/Icon';
import Select from '@/components/base/Select';

const ButtonVariant = {
    default: 'text-icoBlack',
    light: 'text-icoGray-400 text-sm'
};

type ButtonSelectPostTypeProps = Omit<SelectPostTypeProps, 'initialOptions'> & {
    error?: string;
    disabled?: boolean;
    selected?: SelectPostOptionsType;
};

const ButtonSelectPostType = ({
    error,
    disabled,
    selected,
    variant = 'default',
    leadingIcon
}: ButtonSelectPostTypeProps) => {
    const { t } = useTranslation('content');
    const { activeSocialProfile } = useActiveSocialProfile();
    const network: SocialProfileNetworkType = activeSocialProfile?.social_network_type;
    const { albumInfo } = useAlbumContext();

    function getLabel() {
        switch (network) {
            case INSTAGRAM:
                if (albumInfo?.criteria?.album_type === 'stories') {
                    return {
                        iconLabel: 'story',
                        textLabel: 'content:modal.story_type'
                    };
                }
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.post_type'
                };
            case PINTEREST:
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.pin_type'
                };
            case TWITTER:
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.tweet_type'
                };
            default:
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.post_type'
                };
        }
    }

    const { iconLabel, textLabel } = getLabel();

    const labels = useMemo(
        () => selected.map((type) => type.label).join(', '),

        [selected]
    );

    return (
        <Select.Button
            selected={selected}
            disabled={disabled}
            baseStyle={variant === 'default' ? 'noBorder' : 'whiteWithBorder'}
            error={error}>
            <span className="flex items-center space-x-3">
                {leadingIcon ? (
                    <Icon
                        icon={iconLabel}
                        className="text-base text-icoGray-400 dark:text-icoDarkMode-wolf"
                    />
                ) : null}
                <span className={clsx('block truncate dark:text-white', ButtonVariant[variant])}>
                    {`${t(textLabel)}: ${labels}`}
                </span>
            </span>
        </Select.Button>
    );
};

type SelectPostOptionsType = ArrayElement<
    ValueOfObject<ReturnType<typeof postsSortTypes>> | ReturnType<typeof storiesSortTypes>
>[];

type SelectPostTypeProps = {
    leadingIcon?: boolean;
    variant?: 'default' | 'auto';
    initialOptions?: SelectPostOptionsType;
    className?: string;
};

const SelectPostType = ({
    leadingIcon = true,
    variant = 'default',
    initialOptions,
    className
}: SelectPostTypeProps) => {
    const { t } = useTranslation('commons');
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();
    const previousPostTypes = usePrevious(albumInfo?.criteria?.post_types);

    const albumType: 'stories' = albumInfo?.criteria?.album_type;
    const network: SocialProfileNetworkType = activeSocialProfile?.social_network_type;

    const postTypes: SelectPostOptionsType = useMemo(
        () =>
            AlbumFunnelModalService.getPostTypes({
                albumType,
                isGroup,
                selectedSocialProfiles: albumInfo?.criteria?.social_profiles,
                groupSocialProfiles: activeGroup?.social_profiles,
                network,
                translationFunction: t
            }),
        [
            albumType,
            isGroup,
            albumInfo?.criteria?.social_profiles,
            activeGroup?.social_profiles,
            network,
            t
        ]
    );

    const fallbackOptions = useMemo(() => (postTypes.length ? [postTypes[0]] : []), [postTypes]);

    const [selected, setSelected] = useState<SelectPostOptionsType>(
        initialOptions?.map((option) => {
            return { ...option, label: t(option.label) };
        }) ?? []
    );

    useEffect(() => {
        if (!initialOptions && postTypes.length > 0 && selected.length === 0) {
            setSelected(fallbackOptions);
        }
    }, [fallbackOptions]);

    useEffect(() => {
        dispatch({
            type: UPDATE_POST_TYPES,
            payload: selected[0]?.value === 'all' ? [] : selected.map((item) => item.value)
        });
    }, [selected]);

    useEffect(() => {
        const currentPostTypes = albumInfo?.criteria?.post_types;

        if (
            !currentPostTypes?.length &&
            previousPostTypes?.length &&
            selected[0]?.value !== 'all' &&
            fallbackOptions.length > 0
        ) {
            const fallbackValue = fallbackOptions[0]?.value;
            const currentValue = selected[0]?.value;
            if (fallbackValue && fallbackValue !== currentValue) {
                setSelected(fallbackOptions);
            }
        }
    }, [albumInfo?.criteria?.post_types, fallbackOptions, selected, previousPostTypes]);

    const isSelected = (item: ArrayElement<SelectPostOptionsType>['value']) => {
        return !!selected.find((el) => el.value === item);
    };

    const getTypesWithoutAll = () => postTypes.filter((p) => p.value !== 'all');

    const shouldAutoSelectAll = (selectedTypes: SelectPostOptionsType) => {
        const typesWithoutAll = getTypesWithoutAll();
        return typesWithoutAll.every((type) => selectedTypes.some((s) => s.value === type.value));
    };

    const syncAllOption = (selectedTypes: SelectPostOptionsType) => {
        if (shouldAutoSelectAll(selectedTypes)) {
            const allOption = postTypes.find((p) => p.value === 'all');
            return allOption ? [allOption, ...selectedTypes] : selectedTypes;
        }
        return selectedTypes.filter((val) => val.value !== 'all');
    };

    function handleSelected(item: SelectPostOptionsType) {
        if (item.length === 0) {
            setSelected(fallbackOptions);
            return;
        }

        const clickedValue = item[item.length - 1].value;
        const wasSelected = isSelected(clickedValue);

        if (clickedValue === 'all') {
            setSelected(wasSelected ? fallbackOptions : postTypes);
            return;
        }

        const hasAll = isSelected('all');
        let result: SelectPostOptionsType;

        if (hasAll && wasSelected) {
            result = getTypesWithoutAll().filter((val) => val.value !== clickedValue);
        } else if (wasSelected) {
            result = item.filter((val) => val.value !== clickedValue);
            if (result.length === 0) {
                result = fallbackOptions;
            }
        } else {
            result = syncAllOption(item.filter((val) => val.value !== 'all'));
        }

        setSelected(result);
    }

    return (
        <Select
            data-testid="select-post-type"
            selected={selected}
            setSelected={handleSelected}
            options={postTypes}
            buttonBaseStyle="whiteWithBorder"
            noBorder
            button={<ButtonSelectPostType leadingIcon={leadingIcon} variant={variant} />}
            isMultiple={true}
            className={className}
        />
    );
};

export default SelectPostType;
